import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Key, AlertTriangle, RefreshCcw, ArrowLeft, PlayCircle, Wallet, FileText, Info } from "lucide-react";
import { DocumentsPanel } from "@/components/documents/DocumentsPanel";
import { GuestInfoStrip } from "@/components/pms/GuestInfoStrip";
import { CheckoutFlow } from "@/components/checkout/CheckoutFlow";

const STATUS_TONE = {
  initiated: "info", preflight_running: "info", preflight_blocked: "error",
  preflight_passed: "success", settling: "warning", waiting_external: "warning",
  settlement_failed: "error", settled: "success", invoicing: "warning",
  invoice_issued: "success", awaiting_key_return: "warning",
  completed: "success", reverted: "error", cancelled: "neutral",
};

function fmtMinor(m, cur = "INR") {
  if (m == null) return "—";
  return `${cur} ${(Number(m) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(d) { return (d || "").slice(0, 19).replace("T", " "); }
function fmtDuration(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  return m === 0 ? `${sec}s` : `${m}m ${sec % 60}s`;
}

export default function CheckoutWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sess, setSess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "cash" });
  const [keyOpen, setKeyOpen] = useState(false);
  const [keyForm, setKeyForm] = useState({ keys_count: 1, notes: "" });
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [damageOpen, setDamageOpen] = useState(false);
  const [damageNote, setDamageNote] = useState("");
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertReason, setRevertReason] = useState("");
  const [flowOpen, setFlowOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/checkout/${id}`);
      setSess(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runPreflight = () =>
    call(() => api.post(`/checkout/${id}/preflight/run`), "Preflight complete");

  const refreshSettlement = () =>
    call(() => api.post(`/checkout/${id}/settle/refresh`), "Settlement refreshed");

  const capturePayment = () => call(async () => {
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) throw new Error("Enter an amount greater than zero");
    // The user types the amount as-is (e.g. 4000 = ₹4,000); the API stores minor units.
    await api.post(`/checkout/${id}/settle/capture-payment`, {
      amount_minor: Math.round(amt * 100), method: payForm.method,
      idempotency_key: `CO-${id}-${Date.now()}`,
    });
    setPayOpen(false);
    setPayForm({ amount: "", method: "cash" });
  }, "Payment captured");

  const returnKeys = () => call(async () => {
    await api.post(`/checkout/${id}/keys/return`, keyForm);
    setKeyOpen(false);
  }, "Keys returned");

  const addDamage = () => call(async () => {
    await api.post(`/checkout/${id}/damage/note`, { note: damageNote });
    setDamageOpen(false);
    setDamageNote("");
  }, "Damage note added");

  const requestOverride = () => call(async () => {
    await api.post(`/checkout/${id}/preflight/request-override`, {
      reason: overrideReason,
    });
    setOverrideOpen(false);
    setOverrideReason("");
  }, "Override requested");

  const resolveOverride = (approved) => call(async () => {
    await api.post(`/checkout/${id}/preflight/resolve-override`, {
      approved, reason: approved ? "approved" : "rejected",
    });
  }, approved ? "Override approved" : "Override rejected");

  const issueInvoice = () => call(async () => {
    await api.post(`/checkout/${id}/invoice/issue`, {
      invoice_type: "invoice",
    });
  }, "Invoice issued");

  const complete = () => setFlowOpen(true);

  const cancelSession = () => call(async () => {
    await api.post(`/checkout/${id}/cancel`, { reason: "" });
  }, "Session cancelled");

  const revert = () => call(async () => {
    await api.post(`/checkout/${id}/revert`, { reason: revertReason });
    setRevertOpen(false);
    setRevertReason("");
  }, "Session reverted");

  if (loading || !sess) return <div className="p-8 text-hocp-textMuted mono">Loading…</div>;

  const isTerminal = ["completed", "reverted", "cancelled"].includes(sess.status);

  return (
    <div className="max-w-[1800px]">
      <PageHeader
        title={sess.guest_name ? `${sess.guest_name} · Session ${sess.session_no}` : `Session ${sess.session_no}`}
        subtitle={`Checkout Workspace • ${sess.checkout_type} • ${sess.policy_code}${sess.room_number ? ` • Room ${sess.room_number}` : ""}`}
        testid="checkout-ws-header"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav("/checkout/queue")} data-testid="ws-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />Queue
            </Button>
            <Button variant="outline" onClick={load} data-testid="ws-refresh-btn">
              <RefreshCcw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
        }
      />

      <div className="mb-6">
        <GuestInfoStrip name={sess.guest_name} phone={sess.guest_phone} email={sess.guest_email} testid="session-guest-strip"
          right={<Button variant="outline" size="sm" onClick={() => nav(`/pms/stays/${sess.stay_id}`)} data-testid="ws-open-stay">Stay detail</Button>} />
      </div>

      {flowOpen && (
        <CheckoutFlow stayId={sess.stay_id} hotelId={sess.property_id} open onCancel={() => setFlowOpen(false)}
          onSuccess={() => { setFlowOpen(false); load(); }} />
      )}

      {/* Header KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KpiTile label="Status">
          <Badge tone={STATUS_TONE[sess.status] || "neutral"}>{sess.status}</Badge>
        </KpiTile>
        <KpiTile label="Channel">{sess.channel}</KpiTile>
        <KpiTile label="Folios">{(sess.folio_ids || []).length}</KpiTile>
        <KpiTile label="Invoices">{(sess.invoices_issued || []).length}</KpiTile>
        <KpiTile label="Keys">
          {sess.key_return?.returned ? (
            <Badge tone="success">Returned ({sess.key_return.keys_count})</Badge>
          ) : (
            <Badge tone="warning">Pending</Badge>
          )}
        </KpiTile>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* LEFT — Progress + Quick Actions */}
        <div className="col-span-12 lg:col-span-4">
          <Card className="p-5 mb-4">
            <div className="text-xs uppercase tracking-wider text-hocp-textMuted mono mb-4">Progress</div>
            <ProgressRail sess={sess} />
          </Card>

          <Card className="p-5 mb-4">
            <div className="text-xs uppercase tracking-wider text-hocp-textMuted mono mb-4">Quick Actions</div>
            <div className="flex flex-col gap-2">
              <Button
                variant="default"
                onClick={runPreflight}
                disabled={isTerminal}
                data-testid="ws-preflight-btn"
              >
                <PlayCircle className="w-4 h-4 mr-2" />Run Preflight
              </Button>
              <Button
                variant="outline"
                onClick={() => setPayOpen(true)}
                disabled={isTerminal}
                data-testid="ws-capture-payment-btn"
              >
                <Wallet className="w-4 h-4 mr-2" />Capture Payment
              </Button>
              <Button
                variant="outline"
                onClick={refreshSettlement}
                disabled={isTerminal}
                data-testid="ws-refresh-settlement-btn"
              >
                <RefreshCcw className="w-4 h-4 mr-2" />Refresh Settlement
              </Button>
              <Button
                variant="outline"
                onClick={issueInvoice}
                disabled={isTerminal}
                data-testid="ws-issue-invoice-btn"
              >
                <FileText className="w-4 h-4 mr-2" />Issue Invoice
              </Button>
              <Button
                variant="outline"
                onClick={() => setKeyOpen(true)}
                disabled={isTerminal || sess.key_return?.returned}
                data-testid="ws-return-keys-btn"
              >
                <Key className="w-4 h-4 mr-2" />Return Keys
              </Button>
              <Button
                variant="outline"
                onClick={() => setDamageOpen(true)}
                disabled={isTerminal}
                data-testid="ws-damage-note-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />Damage Note
              </Button>
              {sess.status === "preflight_blocked" && (
                <Button
                  variant="outline"
                  onClick={() => setOverrideOpen(true)}
                  data-testid="ws-request-override-btn"
                >
                  Request Override
                </Button>
              )}
              {sess.override?.required && !sess.override?.approved_at && (
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    onClick={() => resolveOverride(true)}
                    className="flex-1"
                    data-testid="ws-approve-override-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />Approve
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => resolveOverride(false)}
                    className="flex-1"
                    data-testid="ws-reject-override-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />Reject
                  </Button>
                </div>
              )}
              <div className="border-t border-hocp-border my-2" />
              {(() => {
                const stayDone = sess.stay_status === "checked_out";
                const reason = stayDone
                  ? `Guest already checked out${sess.stay_checked_out_at ? ` on ${sess.stay_checked_out_at.slice(0, 16).replace("T", " ")}` : ""} — nothing left to do`
                  : isTerminal ? `Session is ${sess.status} — checkout is closed` : null;
                return (
                  <>
                    <Button
                      variant="default"
                      onClick={complete}
                      disabled={!!reason}
                      className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold disabled:bg-[#D5E6E8] disabled:text-[#3F5257] disabled:opacity-100"
                      data-testid="ws-complete-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />{stayDone ? "Checked Out" : "Check Out"}
                    </Button>
                    {reason && (
                      <div className="flex items-start gap-1.5 text-[12px] text-[#3F5257] -mt-1" data-testid="ws-complete-reason">
                        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#97A6A9]" />{reason}
                      </div>
                    )}
                  </>
                );
              })()}
              {!isTerminal && sess.stay_status !== "checked_out" && (
                <Button variant="ghost" onClick={cancelSession} data-testid="ws-cancel-btn">
                  Cancel Session
                </Button>
              )}
              {sess.status === "completed" && (
                <Button
                  variant="destructive"
                  onClick={() => setRevertOpen(true)}
                  data-testid="ws-revert-btn"
                >
                  Revert Completion
                </Button>
              )}
            </div>
          </Card>
        </div>

        {/* RIGHT — Tabs */}
        <div className="col-span-12 lg:col-span-8">
          <Tabs defaultValue="preflight">
            <TabsList className="mb-4">
              <TabsTrigger value="preflight" data-testid="tab-preflight">Preflight</TabsTrigger>
              <TabsTrigger value="settlement" data-testid="tab-settlement">Settlement</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents">Bills & Receipts</TabsTrigger>
              <TabsTrigger value="keys" data-testid="tab-keys">Keys & Damage</TabsTrigger>
              <TabsTrigger value="sla" data-testid="tab-sla">SLA</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            </TabsList>

            <TabsContent value="preflight">
              <PreflightTab sess={sess} />
            </TabsContent>
            <TabsContent value="settlement">
              <SettlementTab sess={sess} />
            </TabsContent>
            <TabsContent value="invoices">
              <InvoicesTab sess={sess} />
            </TabsContent>
            <TabsContent value="documents">
              <Card className="p-5"><DocumentsPanel stayId={sess.stay_id} stay={{ guest_name: sess.guest_name }} canBill={!isTerminal} /></Card>
            </TabsContent>
            <TabsContent value="keys">
              <KeysTab sess={sess} />
            </TabsContent>
            <TabsContent value="sla">
              <SlaTab sess={sess} />
            </TabsContent>
            <TabsContent value="timeline">
              <TimelineTab sess={sess} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Dialogs */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Capture Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount ({sess?.currency || "INR"})</Label>
              <Input
                type="number" min="0" step="0.01" placeholder="e.g. 4000"
                value={payForm.amount}
                onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
                data-testid="pay-amount-input"
              />
            </div>
            <div>
              <Label>Method</Label>
              <Select value={payForm.method} onValueChange={v => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash", "card", "upi", "bank_transfer", "corp_credit"].map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={capturePayment} data-testid="pay-submit-btn">Capture</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={keyOpen} onOpenChange={setKeyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Return Keys</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Keys Count</Label>
              <Input
                type="number"
                value={keyForm.keys_count}
                onChange={e => setKeyForm({ ...keyForm, keys_count: Number(e.target.value) })}
                data-testid="key-count-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={keyForm.notes}
                onChange={e => setKeyForm({ ...keyForm, notes: e.target.value })}
                data-testid="key-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={returnKeys} data-testid="key-submit-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request Override</DialogTitle></DialogHeader>
          <Textarea
            placeholder="Reason (required)"
            value={overrideReason}
            onChange={e => setOverrideReason(e.target.value)}
            data-testid="override-reason-input"
          />
          <DialogFooter>
            <Button onClick={requestOverride} disabled={!overrideReason} data-testid="override-submit-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={damageOpen} onOpenChange={setDamageOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Damage Note</DialogTitle></DialogHeader>
          <Textarea
            placeholder="Describe the damage"
            value={damageNote}
            onChange={e => setDamageNote(e.target.value)}
            data-testid="damage-note-input"
          />
          <DialogFooter>
            <Button onClick={addDamage} disabled={!damageNote} data-testid="damage-submit-btn">Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revertOpen} onOpenChange={setRevertOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Revert Checkout</DialogTitle></DialogHeader>
          <Textarea
            placeholder="Reason (required)"
            value={revertReason}
            onChange={e => setRevertReason(e.target.value)}
            data-testid="revert-reason-input"
          />
          <DialogFooter>
            <Button
              onClick={revert}
              disabled={!revertReason}
              variant="destructive"
              data-testid="revert-submit-btn"
            >
              Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Sub-components =====
function KpiTile({ label, children }) {
  return (
    <Card className="p-4">
      <div className="text-[10px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-2">{label}</div>
      <div className="text-white text-sm">{children}</div>
    </Card>
  );
}

function ProgressRail({ sess }) {
  const steps = [
    { key: "initiated", label: "Initiated" },
    { key: "preflight_passed", label: "Preflight" },
    { key: "settled", label: "Settled" },
    { key: "invoice_issued", label: "Invoice" },
    { key: "awaiting_key_return", label: "Keys" },
    { key: "completed", label: "Completed" },
  ];
  const order = ["initiated", "preflight_running", "preflight_blocked", "preflight_passed",
                  "settling", "waiting_external", "settlement_failed", "settled",
                  "invoicing", "invoice_issued", "awaiting_key_return", "completed"];
  const curIdx = order.indexOf(sess.status);
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const sIdx = order.indexOf(s.key);
        const done = sIdx <= curIdx;
        return (
          <div key={s.key} className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${done ? "bg-emerald-400" : "bg-zinc-700"}`} />
            <div className={`text-sm ${done ? "text-white" : "text-hocp-textMuted"}`}>{s.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function PreflightTab({ sess }) {
  const pf = sess.preflight || {};
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs text-hocp-textMuted mono uppercase tracking-wider">Outcome</div>
          <div className="mt-1">
            {pf.outcome ? (
              <Badge tone={pf.outcome === "PASS" || pf.outcome === "OVERRIDE_APPROVED" ? "success" : pf.outcome === "REQUIRE_APPROVAL" ? "warning" : "error"}>
                {pf.outcome}
              </Badge>
            ) : (
              <span className="text-hocp-textMuted mono text-sm">Not run</span>
            )}
          </div>
        </div>
        <div className="text-xs mono text-hocp-textMuted">{fmtDate(pf.run_at) || "—"}</div>
      </div>
      {(pf.checks || []).length === 0 ? (
        <div className="text-sm text-hocp-textMuted">No checks recorded yet. Run preflight to evaluate policy rules.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-hocp-textMuted">
            <tr>
              <th className="text-left py-2 text-[11px] uppercase tracking-wide">Rule</th>
              <th className="text-left py-2 text-[11px] uppercase tracking-wide">Outcome</th>
              <th className="text-left py-2 text-[11px] uppercase tracking-wide">Detail</th>
            </tr>
          </thead>
          <tbody>
            {pf.checks.map((c, idx) => (
              <tr key={idx} className="border-t border-hocp-border">
                <td className="py-2 mono text-xs">{c.code}</td>
                <td className="py-2">
                  <Badge tone={c.outcome === "PASS" ? "success" : c.outcome === "REQUIRE_APPROVAL" ? "warning" : "error"}>
                    {c.outcome}
                  </Badge>
                </td>
                <td className="py-2 text-xs text-hocp-textMuted">{c.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function SettlementTab({ sess }) {
  const snap = sess.settlement_snapshot || {};
  const per = snap.per_folio || [];
  return (
    <Card className="p-5">
      <div className="text-xs text-hocp-textMuted mono uppercase tracking-wider mb-3">
        Snapshot as of {fmtDate(snap.at) || "never"}
      </div>
      {per.length === 0 ? (
        <div className="text-sm text-hocp-textMuted">No settlement data yet. Refresh to compute.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-hocp-textMuted">
            <tr>
              <th className="text-left py-2 text-[11px] uppercase tracking-wide">Folio</th>
              <th className="text-left py-2 text-[11px] uppercase tracking-wide">Status</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-wide">Outstanding</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-wide">Credit</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-wide">Paid</th>
            </tr>
          </thead>
          <tbody>
            {per.map((s, i) => (
              <tr key={i} className="border-t border-hocp-border">
                <td className="py-2 mono text-xs">{s.folio_no}</td>
                <td className="py-2"><Badge tone={s.settlement_status === "fully_paid" ? "success" : "warning"}>{s.settlement_status}</Badge></td>
                <td className="py-2 text-right mono">{fmtMinor(s.outstanding_minor)}</td>
                <td className="py-2 text-right mono">{fmtMinor(s.credit_balance_minor)}</td>
                <td className="py-2 text-right mono">{fmtMinor(s.total_paid_minor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function InvoicesTab({ sess }) {
  const invs = sess.invoices_issued || [];
  return (
    <Card className="p-5">
      {invs.length === 0 ? (
        <div className="text-sm text-hocp-textMuted">No invoices issued yet.</div>
      ) : (
        <ul className="space-y-2">
          {invs.map((inv, i) => (
            <li key={i} className="flex justify-between items-center py-2 border-b border-hocp-border">
              <div>
                <div className="mono text-sm">{inv.invoice_no}</div>
                <div className="text-xs text-hocp-textMuted">Folio: {inv.folio_id?.slice(-8)}</div>
              </div>
              <Badge tone="info">{inv.invoice_type}</Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function KeysTab({ sess }) {
  const k = sess.key_return || {};
  return (
    <Card className="p-5">
      <div className="grid grid-cols-2 gap-4">
        <KpiTile label="Returned">
          {k.returned ? <Badge tone="success">Yes</Badge> : <Badge tone="warning">No</Badge>}
        </KpiTile>
        <KpiTile label="Count">{k.keys_count ?? "—"}</KpiTile>
      </div>
      {k.notes && (
        <div className="mt-4">
          <div className="text-xs text-hocp-textMuted mono uppercase tracking-wider mb-2">Notes</div>
          <div className="text-sm">{k.notes}</div>
        </div>
      )}
      {k.returned_at && (
        <div className="mt-3 text-xs mono text-hocp-textMuted">Returned at {fmtDate(k.returned_at)}</div>
      )}
    </Card>
  );
}

function SlaTab({ sess }) {
  const sla = sess.sla_timestamps || {};
  const durations = [
    ["Total Duration", sla.arrived_at_desk_at, sla.completed_at],
    ["Preflight", sla.preflight_started_at, sla.preflight_completed_at],
    ["Approval", sla.approval_started_at, sla.approval_completed_at],
    ["Settlement", sla.settlement_started_at, sla.settlement_completed_at],
    ["Invoice", sla.invoice_started_at, sla.invoice_completed_at],
    ["External Wait", sla.external_wait_started_at, sla.external_wait_ended_at],
    ["Room Release", sla.completed_at, sla.room_released_at],
  ];
  function calcSec(a, b) {
    if (!a || !b) return null;
    return Math.max(0, Math.round((new Date(b) - new Date(a)) / 1000));
  }
  return (
    <Card className="p-5">
      <table className="w-full text-sm">
        <thead className="text-hocp-textMuted">
          <tr>
            <th className="text-left py-2 text-[11px] uppercase tracking-wide">Phase</th>
            <th className="text-left py-2 text-[11px] uppercase tracking-wide">Start</th>
            <th className="text-left py-2 text-[11px] uppercase tracking-wide">End</th>
            <th className="text-right py-2 text-[11px] uppercase tracking-wide">Duration</th>
          </tr>
        </thead>
        <tbody>
          {durations.map(([label, a, b]) => (
            <tr key={label} className="border-t border-hocp-border">
              <td className="py-2">{label}</td>
              <td className="py-2 mono text-xs text-hocp-textMuted">{fmtDate(a) || "—"}</td>
              <td className="py-2 mono text-xs text-hocp-textMuted">{fmtDate(b) || "—"}</td>
              <td className="py-2 text-right mono">{fmtDuration(calcSec(a, b))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function TimelineTab({ sess }) {
  const log = [...(sess.step_log || [])].reverse();
  return (
    <Card className="p-5">
      {log.length === 0 ? (
        <div className="text-sm text-hocp-textMuted">No events yet.</div>
      ) : (
        <ul className="space-y-3">
          {log.map((s, i) => (
            <li key={i} className="border-l-2 border-hocp-border pl-4 py-1">
              <div className="flex items-center gap-2">
                <span className="mono text-xs text-hocp-textMuted">{fmtDate(s.at)}</span>
                <Badge tone="info">{s.step}</Badge>
                <span className="text-xs text-hocp-textMuted">{s.from_status} → {s.to_status}</span>
              </div>
              {s.detail && Object.keys(s.detail).length > 0 && (
                <pre className="mt-2 text-[11px] text-hocp-textMuted mono whitespace-pre-wrap">
                  {JSON.stringify(s.detail, null, 2)}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
